import React from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Dropdown, Typography } from "@mimber/ui-web"
import {
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  SendOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import {
  DeleteAnnouncementModal,
  PublishAnnouncementModal,
  ScheduleAnnouncementModal,
  CancelAnnouncementScheduleModal,
} from "@/components/modals"
import { Resource, Action, AnnouncementStatus } from "@mimber/schema"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems/usePermissionedMenuItems.types"
import type { AnnouncementActionsDropdownProps } from "./AnnouncementActionsDropdown.types"

const { Text } = Typography

export const AnnouncementActionsDropdown: React.FunctionComponent<
  AnnouncementActionsDropdownProps
> = ({
  announcement,
  onActionComplete,
  onDeleteSuccess,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const navigate = useNavigate()

  const isDraft = announcement.status === AnnouncementStatus.DRAFT
  const isScheduled = announcement.status === AnnouncementStatus.SCHEDULED

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "preview",
      label: "Preview",
      icon: <EyeOutlined />,
      resource: Resource.ANNOUNCEMENT,
      action: Action.READ,
      onClick: () =>
        navigate({
          to: "/communication/announcements/$announcementUuid/$tabKey",
          params: { announcementUuid: announcement.uuid, tabKey: "preview" },
        }),
    },
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      resource: Resource.ANNOUNCEMENT,
      action: Action.UPDATE,
      disabled: !isDraft,
      onClick: () =>
        isDraft &&
        navigate({
          to: "/communication/announcements/$announcementUuid/edit",
          params: { announcementUuid: announcement.uuid },
        }),
    },
    {
      key: "schedule-tab",
      label: "Schedule",
      icon: <CalendarOutlined />,
      resource: Resource.ANNOUNCEMENT,
      action: Action.READ,
      onClick: () =>
        navigate({
          to: "/communication/announcements/$announcementUuid/$tabKey",
          params: { announcementUuid: announcement.uuid, tabKey: "schedule" },
        }),
    },
    ...(isDraft
      ? ([
          {
            key: "publish",
            label: (
              <PublishAnnouncementModal
                announcement={announcement}
                onSuccess={onActionComplete}
                renderButton={(open) => <span onClick={open}>Publish Now</span>}
              />
            ),
            icon: <SendOutlined />,
            resource: Resource.ANNOUNCEMENT,
            action: Action.UPDATE,
          },
          {
            key: "schedule",
            label: (
              <ScheduleAnnouncementModal
                announcement={announcement}
                onSuccess={onActionComplete}
                renderButton={(open) => (
                  <span onClick={open}>Quick Schedule</span>
                )}
              />
            ),
            icon: <ClockCircleOutlined />,
            resource: Resource.ANNOUNCEMENT,
            action: Action.UPDATE,
          },
        ] as PermissionedMenuItem[])
      : []),
    ...(isScheduled
      ? ([
          {
            key: "cancel-schedule",
            label: (
              <CancelAnnouncementScheduleModal
                announcement={announcement}
                onSuccess={onActionComplete}
                renderButton={(open) => (
                  <span onClick={open}>Cancel Schedule</span>
                )}
              />
            ),
            icon: <StopOutlined />,
            resource: Resource.ANNOUNCEMENT,
            action: Action.UPDATE,
          },
        ] as PermissionedMenuItem[])
      : []),
    {
      key: "delete",
      label: (
        <DeleteAnnouncementModal
          announcement={announcement}
          onSuccess={onDeleteSuccess ?? onActionComplete}
          renderButton={(open) => <span onClick={open}>Delete</span>}
        />
      ),
      icon: <DeleteOutlined />,
      danger: true,
      resource: Resource.ANNOUNCEMENT,
      action: Action.DELETE,
    },
  ]

  const { items } = usePermissionedMenuItems({ items: menuItems })

  if (items.length === 0) {
    return <Text type="secondary">—</Text>
  }

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <Button
        type={type}
        icon={iconOnly ? <MoreOutlined /> : undefined}
        size={size}
        data-testid={`announcement-actions-${announcement.uuid}`}
      >
        {!iconOnly && "Actions"}
      </Button>
    </Dropdown>
  )
}
